// Onboarding cockpit shell. Two tabs that preserve each other's URL state:
//   Runs        — RunList + RunDetail (?run)
//   Prepare a site — site-slug input + SiteOnboardingReadiness (?site)
// Active tab = ?tab if set, else derived (?run wins, else ?site → prepare).

import { useEffect, useState } from "react";

import { PageHeader } from "../../components/shell/PageHeader";
import { EmptyState } from "../../components/ui/EmptyState";
import { SiteOnboardingReadiness } from "./SiteOnboardingReadiness";
import { RunsView } from "./RunsView";

type Tab = "runs" | "prepare";

export function OnboardingCockpit({
  tab,
  site,
  run,
  onTabChange,
  onSiteChange,
  onRunChange,
}: {
  tab: Tab | null;
  site: string | null;
  run: string | null;
  onTabChange: (t: Tab) => void;
  onSiteChange: (slug: string | null) => void;
  onRunChange: (id: string | null) => void;
}) {
  const activeTab: Tab = tab ?? (run ? "runs" : site ? "prepare" : "runs");
  const [input, setInput] = useState(site ?? "");
  useEffect(() => {
    setInput(site ?? "");
  }, [site]);

  return (
    <>
      <PageHeader title="Onboarding" subtitle="Inspect onboarding runs and prepare a site's request.yml." />

      <nav style={tabStrip} aria-label="Onboarding views">
        <button type="button" onClick={() => onTabChange("runs")}
          style={activeTab === "runs" ? activeTabBtn : tabBtn}>Runs</button>
        <button type="button" onClick={() => onTabChange("prepare")}
          style={activeTab === "prepare" ? activeTabBtn : tabBtn}>Prepare a site</button>
      </nav>

      {activeTab === "runs" ? (
        <RunsView run={run} onRunChange={onRunChange} />
      ) : (
        <>
          <form
            style={{ display: "flex", gap: 8, marginBottom: 20, maxWidth: 480 }}
            onSubmit={(e) => {
              e.preventDefault();
              onSiteChange(input.trim() === "" ? null : input.trim());
            }}
          >
            <input
              aria-label="Site slug"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="126-a-bankers-avenue"
              style={{
                flex: 1, padding: "8px 10px", background: "var(--surface-2)",
                border: "1px solid var(--border)", borderRadius: 4, color: "var(--text)",
              }}
            />
            <button type="submit" style={primaryBtn}>Open</button>
          </form>
          {site ? (
            <SiteOnboardingReadiness siteSlug={site} />
          ) : (
            <EmptyState title="Choose a site to onboard" description="Enter the site slug to onboard, e.g. 126-a-bankers-avenue." />
          )}
        </>
      )}
    </>
  );
}

const tabStrip: React.CSSProperties = {
  display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--border)",
};
const tabBtn: React.CSSProperties = {
  padding: "6px 12px", fontSize: "0.85rem", background: "transparent", border: "none",
  borderBottom: "2px solid transparent", color: "var(--text)", cursor: "pointer",
};
const activeTabBtn: React.CSSProperties = {
  ...tabBtn, color: "var(--accent)", borderBottomColor: "var(--accent)", fontWeight: 600,
};
const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
